/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Divider,
  FormControlLabel,
  Paper,
  Popper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export type FilterLocation = { id: string; name: string; tags: string[] };

type Props = {
  locations: FilterLocation[];
  selectedIds: string[];
  appliedTags: string[];
  onChange: (next: { selectedIds: string[]; appliedTags: string[] }) => void;
  /**
   * Which half of the filter to render. The toolbar puts the dropdown inline
   * with the tabs and the tag pills on their own row underneath, so the two
   * halves are mounted separately against the same lifted state.
   */
  section?: 'dropdown' | 'tags';
};

const TEAL = '#61b9c3';
const TEAL_DARK = '#4a9aa3';

type PillState = 'none' | 'some' | 'all';

const PILL_HINTS: Record<PillState, string> = {
  none: ' — click to add',
  some: ' — click to add the rest',
  all: ' — click to remove',
};

const pillState = (selectedCount: number, memberCount: number): PillState => {
  if (memberCount > 0 && selectedCount === memberCount) return 'all';
  return selectedCount > 0 ? 'some' : 'none';
};

const pillBadge = (state: PillState, selectedCount: number, memberCount: number) => {
  if (state === 'all') return '✓';
  if (state === 'some') return `${selectedCount}/${memberCount}`;
  return `(${memberCount})`;
};

const PILL_COLOURS = {
  none: {
    text: 'rgb(60,60,60)', bg: 'transparent', hover: 'rgba(0,0,0,0.04)', badge: '#9aa3a7',
  },
  some: {
    text: TEAL_DARK, bg: '#e3f5f3', hover: '#d3ede9', badge: TEAL_DARK,
  },
  all: {
    text: '#fff', bg: TEAL, hover: TEAL_DARK, badge: '#d6f2ef',
  },
};

const pillStyles = (state: PillState) => ({
  textTransform: 'none' as const,
  borderRadius: '16px',
  border: `1px solid ${state === 'none' ? '#d0d0d0' : TEAL}`,
  padding: '3px 13px',
  minWidth: 0,
  fontSize: '13px',
  fontWeight: 500,
  color: PILL_COLOURS[state].text,
  backgroundColor: PILL_COLOURS[state].bg,
  '&:hover': { backgroundColor: PILL_COLOURS[state].hover, borderColor: TEAL },
});

/**
 * Locations dropdown plus a row of tag pills. Tags are additive shortcuts over
 * the location selection rather than a separate grouping mode: clicking a tag
 * adds its member locations, clicking a fully-applied tag removes the members
 * no other applied tag still holds.
 */
function LocationTagFilter({
  locations, selectedIds, appliedTags, onChange, section = 'dropdown',
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchorRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const allTags = Array.from(new Set(locations.flatMap((loc) => loc.tags || [])))
    .sort((a, b) => a.localeCompare(b));
  const tagMembers = (tag: string) => locations
    .filter((loc) => (loc.tags || []).includes(tag))
    .map((loc) => loc.id);

  const filteredOptions = locations.filter(
    (loc) => loc.name.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const allSelected = locations.length > 0 && selectedIds.length === locations.length;

  const buttonLabel = () => {
    if (locations.length === 0 || allSelected) return 'All locations';
    if (selectedIds.length === 0) return 'No locations selected';
    if (selectedIds.length <= 2) {
      return locations
        .filter((loc) => selectedIds.includes(loc.id))
        .map((loc) => loc.name)
        .join(', ');
    }
    return `${selectedIds.length} locations`;
  };

  // Drop any applied tag left with none of its members selected, so the pills
  // stay honest after locations are unticked by hand.
  const pruneApplied = (nextSelected: string[], nextApplied: string[]) => nextApplied.filter(
    (tag) => tagMembers(tag).some((id) => nextSelected.includes(id)),
  );

  const toggleLocation = (id: string) => {
    const nextSelected = selectedIds.includes(id)
      ? selectedIds.filter((selectedId) => selectedId !== id)
      : [...selectedIds, id];
    onChange({
      selectedIds: nextSelected,
      appliedTags: pruneApplied(nextSelected, appliedTags),
    });
  };

  const applyTag = (tag: string) => {
    const nextSelected = Array.from(new Set([...selectedIds, ...tagMembers(tag)]));
    onChange({
      selectedIds: nextSelected,
      appliedTags: Array.from(new Set([...appliedTags, tag])),
    });
  };

  const removeTag = (tag: string) => {
    const nextApplied = appliedTags.filter((applied) => applied !== tag);
    const heldByOther = new Set(nextApplied.flatMap(tagMembers));
    const nextSelected = selectedIds.filter(
      (id) => !tagMembers(tag).includes(id) || heldByOther.has(id),
    );
    onChange({ selectedIds: nextSelected, appliedTags: nextApplied });
  };

  if (section === 'tags') {
    if (allTags.length === 0) return null;
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
      }}
      >
        <Typography sx={{ fontSize: '13px', color: 'rgb(100,100,100)' }}>
          Tags — click to add locations:
        </Typography>
        {allTags.map((tag) => {
          const members = tagMembers(tag);
          const selectedCount = members.filter((id) => selectedIds.includes(id)).length;
          const state = pillState(selectedCount, members.length);
          const badge = pillBadge(state, selectedCount, members.length);
          const memberNames = locations
            .filter((loc) => members.includes(loc.id))
            .map((loc) => loc.name)
            .join(', ');
          return (
            <Tooltip key={tag} title={`${memberNames}${PILL_HINTS[state]}`} arrow>
              <span>
                <Button
                  size="small"
                  disabled={members.length === 0}
                  onClick={() => (state === 'all' ? removeTag(tag) : applyTag(tag))}
                  sx={pillStyles(state)}
                >
                  {tag}
                  <Box
                    component="span"
                    sx={{
                      marginLeft: '7px',
                      fontSize: '11.5px',
                      color: PILL_COLOURS[state].badge,
                    }}
                  >
                    {badge}
                  </Box>
                </Button>
              </span>
            </Tooltip>
          );
        })}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Button
        ref={anchorRef}
        onClick={() => setOpen((prev) => !prev)}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          textTransform: 'none',
          color: allSelected ? 'rgb(60,60,60)' : '#fff',
          backgroundColor: allSelected ? 'transparent' : TEAL,
          border: '1px solid #d5dbdd',
          borderRadius: '8px',
          padding: '6px 12px',
          fontWeight: 400,
          fontSize: '13px',
          minWidth: '180px',
          justifyContent: 'space-between',
          '&:hover': {
            backgroundColor: allSelected ? 'rgba(0,0,0,0.04)' : TEAL_DARK,
            borderColor: TEAL,
          },
        }}
      >
        {buttonLabel()}
      </Button>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            sx={{
              width: 280,
              marginTop: '4px',
              borderRadius: '12px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ padding: '10px 12px' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search locations"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
            <Divider />
            <Box sx={{ maxHeight: 260, overflowY: 'auto', padding: '4px 12px' }}>
              {filteredOptions.length === 0 && (
                <Typography sx={{ padding: '12px 4px', color: '#999', fontSize: '14px' }}>
                  No results found.
                </Typography>
              )}
              {filteredOptions.map((loc) => (
                <FormControlLabel
                  key={loc.id}
                  sx={{ display: 'flex', width: '100%' }}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(loc.id)}
                      onChange={() => toggleLocation(loc.id)}
                      sx={{ color: '#b0b0b0', '&.Mui-checked': { color: TEAL } }}
                    />
                  )}
                  label={loc.name}
                />
              ))}
            </Box>
            <Divider />
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', padding: '10px 16px',
            }}
            >
              <Typography
                onClick={() => onChange({
                  selectedIds: locations.map((loc) => loc.id),
                  appliedTags,
                })}
                sx={{
                  color: TEAL, fontWeight: 500, fontSize: '14px', cursor: 'pointer',
                }}
              >
                Select all
              </Typography>
              <Typography
                onClick={() => onChange({ selectedIds: [], appliedTags: [] })}
                sx={{
                  color: TEAL, fontWeight: 500, fontSize: '14px', cursor: 'pointer',
                }}
              >
                Clear all
              </Typography>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}

export default LocationTagFilter;
